import React, { useState, useRef, useMemo, useContext } from 'react'
import { validateFunc } from '../../constraints/constraints'
import { withTranslation } from 'react-i18next'
import { useMutation, gql, useQuery } from '@apollo/client'
import {
  createRestaurant,
  getCuisines,
  restaurantByOwner,
  getActiveShopTypes
} from '../../apollo'

import {
  Box,
  Alert,
  Typography,
  Button,
  Input,
  Switch,
  Grid,
  Checkbox,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  ListItemText
} from '@mui/material'

import ConfigurableValues from '../../config/constants'

import useStyles from './styles'
import useGlobalStyles from '../../utils/globalStyles'
import InputAdornment from '@mui/material/InputAdornment'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { SHOP_TYPE } from '../../utils/enums'
import StoreTypeField from './StoreTypeField'
import Dropdown, { selectMatchInputSx } from '../Dropdown'
import FormCardHeader from '../FormCardHeader'
import AddPhotoAlternateOutlined from '@mui/icons-material/AddPhotoAlternateOutlined'
import ConfigurationContext from '../../context/Configuration'

// A type="number" box accepts a typed minus sign, and a negative fee would be
// subtracted from the customer's bill. Refuse it at the keyboard and clamp
// anything pasted in. Mirrors the same guards on the store profile form.
const blockNegativeKeys = event => {
  if (['-', '+', 'e', 'E'].includes(event.key)) event.preventDefault()
}

const clampNegative = event => {
  const value = event.target.value
  if (value === '') return
  const numeric = Number(value)
  event.target.value = Number.isNaN(numeric) ? '' : String(Math.max(0, numeric))
}

const positiveNumberProps = { min: 0, onKeyDown: blockNegativeKeys }

// Unit shown inside a field (min, %, the currency) so the number is never
// ambiguous.
const unitAdornment = (unit, position) =>
  unit ? (
    <InputAdornment position={position}>
      <Typography
        component="span"
        sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>
        {unit}
      </Typography>
    </InputAdornment>
  ) : null

// A type="number" box accepts a typed minus sign, and a negative fee would be
// subtracted from the customer's bill. Refuse it at the keyboard and clamp
// anything pasted in. Mirrors the same guards on the store profile form.

const CREATE_RESTAURANT = gql`
  ${createRestaurant}
`
const RESTAURANT_BY_OWNER = gql`
  ${restaurantByOwner}
`
const CUISINES = gql`
  ${getCuisines}
`
const ACTIVE_SHOP_TYPES = gql`
  ${getActiveShopTypes}
`
// Shop Category drives the restaurant vs store behaviour in the apps
// (Borku Stores rail, list screens, checkout fee label)
const SHOP_CATEGORY_OPTIONS = [
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'grocery', label: 'Store' }
]
const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250
    }
  }
}

const CreateRestaurant = props => {
  const { CLOUDINARY_UPLOAD_URL, CLOUDINARY_FOOD } = ConfigurableValues()

  const { t } = props
  const configuration = useContext(ConfigurationContext)
  const currencyUnit =
    configuration?.currencySymbol || configuration?.currency || ''

  const userType = localStorage.getItem('user-borku')
    ? JSON.parse(localStorage.getItem('user-borku')).userType
    : null

  const owner = props.owner
  const [showPassword, setShowPassword] = useState(false)
  const [imgUrl, setImgUrl] = useState('')
  const [nameError, setNameError] = useState(null)
  const [usernameError, setUsernameError] = useState(null)
  const [passwordError, setPasswordError] = useState(null)
  const [addressError, setAddressError] = useState(null)
  const [deliveryTimeError, setDeliveryTimeError] = useState(null)
  const [minimumOrderError, setMinimumOrderError] = useState(null)
  const [salesTaxError, setSalesTaxError] = useState(null)
  const [phoneError, setPhoneError] = useState(null)
  const [webURLError, setWebURLError] = useState(null)
  const [bagFeeError, setBagFeeError] = useState(null)
  const [imageError, setImageError] = useState(null)
  const [restaurantCuisines, setRestaurantCuisines] = React.useState([])

  // const [shopType, setShopType] = useState(SHOP_TYPE.RESTAURANT)
  const [errors, setErrors] = useState('')
  const [success, setSuccess] = useState('')
  const onCompleted = data => {
    console.log('on complete here')
    setNameError(null)
    setAddressError(null)
    setUsernameError(null)
    setPasswordError(null)
    setDeliveryTimeError(null)
    setMinimumOrderError(null)
    setErrors('')
    setSalesTaxError(null)
    setPhoneError(null)
    setWebURLError(null)
    setBagFeeError(null)
    setImageError(null)
    clearFormValues()
    // Hand the confirmation to the page and close: the modal unmounts, so the
    // next "Add" starts from an empty form instead of the last store's data.
    if (props.onCreated) {
      props.onCreated(t('RestaurantAdded'))
      return
    }
    setSuccess(t('RestaurantAdded'))
    setTimeout(hideAlert, 5000)
  }
  const onError = ({ graphQLErrors, networkError }) => {
    console.log('graphQLErrors', graphQLErrors)
    console.log('networkErrors', networkError)
    setNameError(null)
    setAddressError(null)
    setUsernameError(null)
    setPasswordError(null)
    setDeliveryTimeError(null)
    setMinimumOrderError(null)
    setSalesTaxError(null)
    setPhoneError(null)
    setWebURLError(null)
    setBagFeeError(null)
    setImageError(null)
    setSuccess('')
    if (graphQLErrors && graphQLErrors.length) {
      setErrors(graphQLErrors[0].message)
    }
    if (networkError) {
      setErrors(t('NetworkError'))
    }
    setTimeout(hideAlert, 5000)
  }
  const hideAlert = () => {
    setErrors('')
    setSuccess('')
  }

  const { data: cuisines } = useQuery(CUISINES)
  // Store Type picks one of these (the home screen tile the store is listed
  // under); Shop Category below stays restaurant / store
  const { data: shopTypesData } = useQuery(ACTIVE_SHOP_TYPES)
  const activeShopTypes = shopTypesData?.activeShopTypes ?? []
  const [storeType, setStoreType] = useState('')
  const cuisinesInDropdown = useMemo(
    () => cuisines?.cuisines?.map(item => item.name),
    [cuisines]
  )
  console.log('cuisines => ', cuisinesInDropdown)

  const [mutate, { loading }] = useMutation(CREATE_RESTAURANT, {
    onError,
    onCompleted,
    refetchQueries: [
      {
        query: RESTAURANT_BY_OWNER,
        variables: { id: owner }
      }
    ],
    awaitRefetchQueries: true
  })

  const formRef = useRef(null)

  const selectImage = (event, state) => {
    const result = filterImage(event)
    if (result) imageToBase64(result)
  }

  const filterImage = event => {
    let images = []
    for (var i = 0; i < event.target.files.length; i++) {
      images[i] = event.target.files.item(i)
    }
    images = images.filter(image => image.name.match(/\.(jpg|jpeg|png|gif)$/))
    return images.length ? images[0] : undefined
  }
  const imageToBase64 = imgUrl => {
    const fileReader = new FileReader()
    fileReader.onloadend = () => {
      setImgUrl(fileReader.result)
    }
    fileReader.readAsDataURL(imgUrl)
  }
  const uploadImageToCloudinary = async () => {
    if (imgUrl === '') return imgUrl

    const apiUrl = `${CLOUDINARY_UPLOAD_URL}/image/upload`
    const data = {
      file: imgUrl,
      upload_preset: CLOUDINARY_FOOD
    }
    try {
      const result = await fetch(apiUrl, {
        body: JSON.stringify(data),
        headers: {
          'content-type': 'application/json'
        },
        method: 'POST'
      })
      const imageData = await result.json()
      return imageData.secure_url
    } catch (e) {
      console.log(e)
    }
  }

  const onSubmitValidaiton = data => {
    const form = formRef.current
    const name = form.name.value
    const address = form.address.value
    const username = form.username.value
    const password = form.password.value
    // IMPORTANT!!!!
    const deliveryTime = form.deliveryTime.value
    const minimumOrder = form.minimumOrder.value
    const salesTax = form.salesTax?.value ? +form.salesTax.value : 0
    const phone = form.phone?.value || ''
    const webURL = form.webURL?.value || ''
    const bagFee = form.bagFee?.value || ''

    const nameError = !validateFunc({ name }, 'name')
    const addressError = !validateFunc({ address }, 'address')

    const deliveryTimeError = !validateFunc(
      { deliveryTime: deliveryTime },
      'deliveryTime'
    )
    const minimumOrderError = !validateFunc(
      { minimumOrder: minimumOrder },
      'minimumOrder'
    )
    const usernameError = !validateFunc({ name: username }, 'name')
    const passwordError = !validateFunc({ password }, 'password')
    const salesTaxError =
      userType == 'ADMIN' ? !validateFunc({ salesTax }, 'salesTax') : true
    const phoneError = phone ? true : false
    const webURLError = webURL ? true : false
    const bagFeeError = bagFee ? true : false
    setNameError(nameError)
    setAddressError(addressError)
    setUsernameError(usernameError)
    setPasswordError(passwordError)
    setDeliveryTimeError(deliveryTimeError)
    setMinimumOrderError(minimumOrderError)
    setSalesTaxError(salesTaxError)
    setPhoneError(phoneError)
    setWebURLError(webURLError)
    setBagFeeError(bagFeeError)
    if (
      !(
        nameError &&
        addressError &&
        usernameError &&
        passwordError &&
        deliveryTimeError &&
        minimumOrderError &&
        salesTaxError &&
        phoneError &&
        webURLError &&
        bagFeeError
      )
    ) {
      setErrors(t('FieldsRequired'))
    }
    return (
      nameError &&
      addressError &&
      usernameError &&
      passwordError &&
      deliveryTimeError &&
      minimumOrderError &&
      salesTaxError &&
      phoneError &&
      webURLError &&
      bagFeeError
    )
  }
  function update(cache, { data: { createRestaurant } }) {
    try {
      const { restaurantByOwner } = cache.readQuery({
        query: RESTAURANT_BY_OWNER,
        variables: { id: owner }
      })
      if (restaurantByOwner) {
        cache.writeQuery({
          query: RESTAURANT_BY_OWNER,
          variables: { id: owner },
          data: {
            restaurantByOwner: {
              ...restaurantByOwner,
              restaurants: [...restaurantByOwner.restaurants, createRestaurant]
            }
          }
        })
      }
    } catch (error) {
      console.log('Cache update error:', error)
    }
  }
  const clearFormValues = () => {
    const form = formRef.current
    form.name.value = ''
    form.address.value = ''
    form.username.value = ''
    form.password.value = ''
    form.deliveryTime.value = 20
    form.minimumOrder.value = 0
    if (form.phone) form.phone.value = ''
    if (form.webURL) form.webURL.value = ''
    if (form.bagFee) form.bagFee.value = ''
    setImgUrl('')
    setImageError(null)
  }

  const handleCuisineChange = event => {
    const {
      target: { value }
    } = event
    setRestaurantCuisines(typeof value === 'string' ? value.split(',') : value)
  }

  const classes = useStyles()
  const globalClasses = useGlobalStyles()

  return (
    <Box container className={classes.container}>
      <FormCardHeader
        className={classes.heading}
        title={t('AddRestaurant')}
        onClose={props.onClose}
        action={
          <Box className={globalClasses.flexRow} sx={{ gap: 0.5 }}>
            <label>{t('Available')}</label>
            <Switch defaultChecked style={{ color: 'black' }} />
          </Box>
        }
      />

      <Box className={classes.form}>
        <form ref={formRef}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography className={classes.labelText}>
                  {t('Username')}
                </Typography>
                <Input
                  style={{ marginTop: -1 }}
                  name="username"
                  id="input-type-username"
                  placeholder={t('RestaurantUsername')}
                  type="text"
                  defaultValue={''}
                  disableUnderline
                  className={[
                    globalClasses.input,
                    usernameError === false
                      ? globalClasses.inputError
                      : usernameError === true
                      ? globalClasses.inputSuccess
                      : ''
                  ]}
                  onChange={event => {
                    event.target.value = event.target.value
                      .trim()
                      .replace(/\s/g, '')
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography className={classes.labelText}>
                  {t('Password')}
                </Typography>
                <Input
                  style={{ marginTop: -1 }}
                  name="password"
                  id="input-type-password"
                  placeholder={t('RestaurantPassword')}
                  type={showPassword ? 'text' : 'password'}
                  defaultValue={''}
                  disableUnderline
                  className={[
                    globalClasses.input,
                    passwordError === false
                      ? globalClasses.inputError
                      : passwordError === true
                      ? globalClasses.inputSuccess
                      : ''
                  ]}
                  endAdornment={
                    <InputAdornment position="end">
                      <Checkbox
                        checked={showPassword}
                        onChange={() => setShowPassword(!showPassword)}
                        color="primary"
                        icon={<VisibilityOffIcon />}
                        checkedIcon={<VisibilityIcon />}
                      />
                    </InputAdornment>
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography className={classes.labelText}>
                  {t('Name')}
                </Typography>
                <Input
                  style={{ marginTop: -1 }}
                  name="name"
                  id="input-type-name"
                  placeholder={t('RestaurantName')}
                  type="text"
                  defaultValue={''}
                  disableUnderline
                  className={[
                    globalClasses.input,
                    nameError === false
                      ? globalClasses.inputError
                      : nameError === true
                      ? globalClasses.inputSuccess
                      : ''
                  ]}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography className={classes.labelText}>
                  {t('Address')}
                </Typography>
                <Input
                  style={{ marginTop: -1 }}
                  name="address"
                  id="input-type-address"
                  placeholder={t('RestaurantAddress')}
                  type="text"
                  defaultValue={''}
                  disableUnderline
                  className={[
                    globalClasses.input,
                    addressError === false
                      ? globalClasses.inputError
                      : addressError === true
                      ? globalClasses.inputSuccess
                      : ''
                  ]}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography className={classes.labelText}>
                  {t('DeliveryTime')}
                </Typography>
                <Input
                  style={{ marginTop: -1 }}
                  name="deliveryTime"
                  id="input-type-delivery-time"
                  placeholder={t('DeliveryTime')}
                  type="number"
                  inputProps={positiveNumberProps}
                  onBlur={clampNegative}
                  endAdornment={unitAdornment('min', 'end')}
                  disableUnderline
                  className={[
                    globalClasses.input,
                    deliveryTimeError === false
                      ? globalClasses.inputError
                      : deliveryTimeError === true
                      ? globalClasses.inputSuccess
                      : ''
                  ]}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography className={classes.labelText}>
                  {t('MinOrder')}
                </Typography>
                <Input
                  style={{ marginTop: -1 }}
                  name="minimumOrder"
                  id="input-type-minimum-order"
                  placeholder={t('MinOrder')}
                  type="number"
                  inputProps={positiveNumberProps}
                  onBlur={clampNegative}
                  startAdornment={unitAdornment(currencyUnit, 'start')}
                  disableUnderline
                  className={[
                    globalClasses.input,
                    minimumOrderError === false
                      ? globalClasses.inputError
                      : minimumOrderError === true
                      ? globalClasses.inputSuccess
                      : ''
                  ]}
                />
              </Box>
            </Grid>
            {userType == 'ADMIN' && (
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography className={classes.labelText}>
                    {t('ServiceFee')}
                  </Typography>
                  <Input
                    style={{ marginTop: -1 }}
                    name="salesTax"
                    id="input-type-sales-tax"
                    placeholder={t('ServiceFee')}
                    type="number"
                    inputProps={positiveNumberProps}
                    onBlur={clampNegative}
                    endAdornment={unitAdornment('%', 'end')}
                    disableUnderline
                    className={[
                      globalClasses.input,
                      salesTaxError === false
                        ? globalClasses.inputError
                        : salesTaxError === true
                        ? globalClasses.inputSuccess
                        : ''
                    ]}
                  />
                </Box>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <Dropdown
                title={t('Shop Category')}
                values={SHOP_CATEGORY_OPTIONS}
                defaultValue={SHOP_TYPE.RESTAURANT}
                id={'shop-type'}
                name={'shopType'}
                displayEmpty={true}
              />
              <Box mt={2}>
                <StoreTypeField
                  shopTypes={activeShopTypes}
                  value={storeType}
                  onChange={setStoreType}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography className={classes.labelText}>
                  {t('Cuisines')}
                </Typography>
                <Select
                  multiple
                  value={restaurantCuisines}
                  onChange={handleCuisineChange}
                  displayEmpty
                  input={<OutlinedInput />}
                  renderValue={selected =>
                    selected.length ? (
                      selected.join(', ')
                    ) : (
                      <Typography
                        component="span"
                        sx={{ fontSize: 14, color: 'text.disabled' }}>
                        {t('Cuisines')}
                      </Typography>
                    )
                  }
                  MenuProps={MenuProps}
                  className={globalClasses.input}
                  sx={selectMatchInputSx}>
                  {cuisinesInDropdown?.map(cuisine => (
                    <MenuItem
                      key={'restaurant-cuisine-' + cuisine}
                      value={cuisine}
                      style={{ color: '#000000', textTransform: 'capitalize' }}>
                      <Checkbox
                        checked={restaurantCuisines.indexOf(cuisine) > -1}
                      />
                      <ListItemText primary={cuisine} />
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography className={classes.labelText}>
                  {t('Phone')}
                </Typography>
                <Input
                  style={{ marginTop: -1 }}
                  name="phone"
                  id="input-type-phone"
                  placeholder={t('RestaurantPhone')}
                  type="tel"
                  defaultValue={''}
                  disableUnderline
                  className={[
                    globalClasses.input,
                    phoneError === false
                      ? globalClasses.inputError
                      : phoneError === true
                      ? globalClasses.inputSuccess
                      : ''
                  ]}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography className={classes.labelText}>
                  {t('Website URL')}
                </Typography>
                <Input
                  style={{ marginTop: -1 }}
                  name="webURL"
                  id="input-type-weburl"
                  placeholder={t('RestaurantWebURL')}
                  type="text"
                  defaultValue={''}
                  disableUnderline
                  className={[
                    globalClasses.input,
                    webURLError === false
                      ? globalClasses.inputError
                      : webURLError === true
                      ? globalClasses.inputSuccess
                      : ''
                  ]}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography className={classes.labelText}>
                  {t('Bag Fee')}
                </Typography>
                <Input
                  style={{ marginTop: -1 }}
                  name="bagFee"
                  id="input-type-bagfee"
                  placeholder={t('RestaurantBagFee')}
                  type="number"
                  inputProps={positiveNumberProps}
                  onBlur={clampNegative}
                  defaultValue={''}
                  startAdornment={unitAdornment(currencyUnit, 'start')}
                  disableUnderline
                  className={[
                    globalClasses.input,
                    bagFeeError === false
                      ? globalClasses.inputError
                      : bagFeeError === true
                      ? globalClasses.inputSuccess
                      : ''
                  ]}
                />
              </Box>
            </Grid>
          </Grid>

          <Box
            mt={3}
            style={{ alignItems: 'center' }}
            className={globalClasses.flex}>
            {imgUrl ? (
              <img
                className={classes.image}
                alt={t('Image')}
                style={
                  imageError === false ? { border: '1px solid #d32f2f' } : {}
                }
                src={imgUrl}
              />
            ) : (
              <Box
                className={classes.imagePlaceholder}
                sx={imageError === false ? { borderColor: '#d32f2f' } : {}}>
                <AddPhotoAlternateOutlined sx={{ fontSize: 30 }} />
                <Typography sx={{ fontSize: 11, fontWeight: 600 }}>
                  {t('NoImageYet')}
                </Typography>
              </Box>
            )}
            <label htmlFor="file-upload" className={classes.fileUpload}>
              {t('UploadAnImage')}
            </label>
            <input
              className={classes.file}
              id="file-upload"
              type="file"
              accept="image/*"
              onChange={event => {
                selectImage(event, 'image_url')
                setImageError(true)
              }}
            />
          </Box>
          <Box>
            <Button
              className={globalClasses.button}
              disabled={loading}
              onClick={async e => {
                e.preventDefault()
                const isValid = onSubmitValidaiton()
                if (isValid) {
                  const imgUpload = await uploadImageToCloudinary()
                  const form = formRef.current
                  const name = form.name.value
                  const address = form.address.value
                  const deliveryTime = form.deliveryTime.value
                  const minimumOrder = form.minimumOrder.value
                  const username = form.username.value
                  const password = form.password.value
                  const shopType = form.shopType.value
                  const categoryType = storeType.trim()
                  const phone = form.phone?.value || ''
                  const webURL = form.webURL?.value || ''
                  const bagFee = form.bagFee?.value || '0'
                  mutate({
                    variables: {
                      owner,
                      restaurant: {
                        name,
                        address,
                        image: imgUpload || '',
                        deliveryTime: Number(deliveryTime),
                        minimumOrder: Number(minimumOrder),
                        ...(form.salesTax?.value !== undefined &&
                        form.salesTax.value !== ''
                          ? { salesTax: Math.max(0, +form.salesTax.value) }
                          : {}),
                        username,
                        password,
                        shopType,
                        categoryType,
                        cuisines: restaurantCuisines,
                        phone,
                        webURL,
                        bagFee: parseFloat(bagFee) || 0
                      }
                    }
                  })
                }
              }}>
              {t('Save')}
            </Button>
          </Box>
        </form>
        <Box mt={2}>
          {success && (
            <Alert
              className={globalClasses.alertSuccess}
              variant="filled"
              severity="success">
              {success}
            </Alert>
          )}
          {errors && (
            <Alert
              className={globalClasses.alertError}
              variant="filled"
              severity="error">
              {errors}
            </Alert>
          )}
        </Box>
      </Box>
    </Box>
  )
}
export default withTranslation()(CreateRestaurant)
